import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import EnquiryForm from "@/components/EnquiryForm";

const TIMEZONES = [
  { city: "Trichy", tz: "Asia/Kolkata", hq: true },
  { city: "Singapore", tz: "Asia/Singapore" },
  { city: "London", tz: "Europe/London" },
  { city: "San Francisco", tz: "America/Los_Angeles" },
];

const CONTACT_ITEMS = [
  {
    icon: Mail,
    label: "Enterprise inquiries",
    value: "info@jisll.com",
    href: "mailto:info@jisll.com",
    testid: "contact-item-email",
  },
  {
    icon: Phone,
    label: "Call us",
    value: "+91 94862 60638",
    href: "tel:+919486260638",
    testid: "contact-item-phone",
  },
  {
    icon: MapPin,
    label: "Office",
    value: "78, 1st Floor, 2nd Main Road, M M Nagar, Trichy - 620017",
    href: null,
    testid: "contact-item-address",
  },
];

const EASE = [0.16, 1, 0.3, 1];

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.7, ease: EASE, delay },
});

export default function Contact() {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(i);
  }, []);

  const time = (tz) =>
    new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: tz,
    }).format(now);

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="scroll-mt-24 border-t border-slate-200/70 bg-slate-50/70 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-6">
            <motion.p
              {...reveal()}
              className="mb-5 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand"
            >
              <span className="h-px w-10 bg-brand" />
              Contact
            </motion.p>
            <motion.h2
              {...reveal(0.05)}
              className="font-heading text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12]"
            >
              Let's make your systems boring.
            </motion.h2>
            <motion.p
              {...reveal(0.1)}
              className="mt-6 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              Tell us what keeps failing, what keeps you up at night, or what
              you are about to launch. We respond within one business day.
            </motion.p>

            <div className="mt-10 space-y-4">
              {CONTACT_ITEMS.map((item, i) => (
                <motion.div key={item.testid} {...reveal(0.08 * i)}>
                  <a
                    href={item.href || undefined}
                    data-testid={item.testid}
                    className={`flex items-start gap-5 rounded-3xl border border-slate-200/80 bg-white p-6 transition-all duration-200 ${
                      item.href
                        ? "hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-[0_16px_40px_rgba(0,82,255,0.08)]"
                        : "cursor-default"
                    }`}
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-50 text-brand">
                      <item.icon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400">
                        {item.label}
                      </span>
                      <span className="mt-1 block break-words font-heading text-base font-bold text-ink sm:text-lg">
                        {item.value}
                      </span>
                    </span>
                    {item.href && (
                      <ArrowUpRight className="ml-auto h-4 w-4 shrink-0 text-slate-300" />
                    )}
                  </a>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div {...reveal(0.12)} className="lg:col-span-6">
            <EnquiryForm />
            <div
              data-testid="contact-global-clock-card"
              className="relative mt-4 overflow-hidden rounded-3xl border border-slate-200/80"
            >
              <img
                src="https://images.unsplash.com/photo-1549791084-5f78368b208b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
                alt="Architectural structure representing JISLL's global presence"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/60 to-ink/40" />
              <div className="relative p-6 sm:p-8">
                <div className="mb-4 flex items-center gap-3">
                  <span className="h-2 w-2 animate-pulse-dot rounded-full bg-emerald-400" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300">
                    Follow-the-sun support · 24/7
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {TIMEZONES.map((t) => (
                    <div
                      key={t.city}
                      data-testid={`contact-clock-${t.city.toLowerCase().replace(" ", "-")}`}
                      className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/10 px-5 py-3.5 backdrop-blur-md transition-colors duration-200 hover:bg-white/15"
                    >
                      <span className="flex items-baseline gap-2.5">
                        <span className="font-heading text-base font-bold text-white">
                          {t.city}
                        </span>
                        {t.hq && (
                          <span className="rounded-full bg-brand px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.18em] text-white">
                            HQ
                          </span>
                        )}
                      </span>
                      <span className="font-mono text-sm tabular-nums text-slate-200">
                        {time(t.tz)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
